import { useAuth } from '../contexts/AuthContext';

/**
 * Metadata delle feature UX disponibili
 * Usato per label, icone, descrizioni e filtro per ruolo
 */
const FEATURE_META = {
  quickCheckin: {
    label: 'Quick Check-in',
    icon: '⚡',
    desc: 'Segna le presenze con una griglia rapida',
    roles: ['educatore', 'coordinatore'],
    category: 'compilazione',
  },
  attivitaGruppo: {
    label: 'Attività di Gruppo',
    icon: '👥',
    desc: "Registra un'attività per più minori insieme",
    roles: ['educatore', 'coordinatore'],
    category: 'compilazione',
  },
  smartAlerts: {
    label: 'Smart Alerts',
    icon: '🔔',
    desc: 'Insight automatici su pattern e anomalie',
    roles: ['educatore', 'coordinatore'],
    category: 'monitoraggio',
  },
  prepColloquio: {
    label: 'Prep Colloquio',
    icon: '📋',
    desc: 'Dossier auto-generato per colloqui',
    roles: ['coordinatore'],
    category: 'monitoraggio',
  },
};

/**
 * Messaggi di blocco per livello
 */
const LOCK_MESSAGES = {
  global: 'Funzionalità in sviluppo',
  plan: 'Non inclusa nel tuo piano',
  tenant: 'Disabilitata dal coordinatore',
};

/**
 * Hook per verificare la visibilità di una feature UX
 *
 * Formula: visible = globalEnabled && planIncludes && tenantEnabled && userActive && roleMatch
 *
 * 4 livelli:
 * 1. Global (SystemSettings) — kill-switch SuperAdmin
 * 2. Piano (PlanConfig.features) — inclusa nel piano
 * 3. Centro (TenantSettings.enabledFeatures) — abilitata dal coordinatore
 * 4. Utente (User.uiPreferences.features) — preferenza personale
 *
 * reason: 'global' | 'plan' | 'tenant' | null
 */
export function useFeature(featureName) {
  const { user, tenant } = useAuth();

  // Livello 1: Globale (SuperAdmin)
  const globalFeatures = tenant?.globalFeatures || {};
  const globalEnabled = globalFeatures[featureName] !== false;

  // Livello 2: Piano
  const planFeatures = tenant?.planFeatures || {};
  const available = planFeatures[featureName] !== false;

  // Livello 3: Centro (coordinatore)
  const tenantFeatures = tenant?.settings?.enabledFeatures || {};
  const enabled = tenantFeatures[featureName] !== false;

  // Livello 4: Utente
  const userFeatures = user?.uiPreferences?.features || {};
  const active = userFeatures[featureName] !== false;

  // Ruolo compatibile
  const meta = FEATURE_META[featureName] || {};
  const userRole = user?.role || 'educatore';
  const roleMatch = !meta.roles || meta.roles.includes(userRole);

  // Determina il motivo del blocco (priorità top-down)
  let reason = null;
  if (!globalEnabled) reason = 'global';
  else if (!available) reason = 'plan';
  else if (!enabled) reason = 'tenant';

  const locked = reason !== null;
  const lockMessage = reason ? LOCK_MESSAGES[reason] : null;

  return {
    visible: globalEnabled && available && enabled && active && roleMatch,
    globalEnabled, // abilitata globalmente (SuperAdmin)
    available,     // inclusa nel piano
    enabled,       // abilitata nel centro
    active,        // attivata dall'utente
    locked,        // bloccata da qualche livello superiore
    reason,        // 'global' | 'plan' | 'tenant' | null
    lockMessage,   // messaggio localizzato
    roleMatch,
    meta,
  };
}

/**
 * Hook per ottenere lo stato di tutte le feature
 * Usato nella pagina preferenze per mostrare la lista completa
 */
export function useAllFeatures() {
  const features = {};
  for (const key of Object.keys(FEATURE_META)) {
    features[key] = useFeature(key);
  }
  return features;
}

export { FEATURE_META };
